import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  finishSection,
  finishQuiz,
  fetchQuizMinimalData,
  cleanUpAfterQuizEnds
} from '@actions/quizTaking';
import {
  getQuizDetails,
  getCurrentSessionUID,
  getSelectedLanguage,
  getIsQuizForPlusUsers,
  getIsUserEnrolledInQuiz,
  getCurrentQuizType
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import { TEST_FINISHED } from '@constants/segment';
import pushRoute from '@utils/pushRoute';
import { getIsGTPGoal } from '@api/selectors/constants';
import { getIsDSTFlow } from '@cont/DST/utils';
import { useDSTMetaInfoStore } from '@stores/dst/dstStore';
import useConstants from '@api/hooks/useConstants';
import useQuizSegmentAnalytics from '../../Common/useQuizSegmentAnalytics';
import useOngoingSection from './useOngoingSection';
import useFailedAPIQueue from './useFailedAPIQueue';
import useTotalSummary from './useTotalSummary';

const useFinishQuiz = () => {
  const dispatch = useDispatch();
  const { query, asPath } = useRouter();
  const isDSTFlow = getIsDSTFlow(asPath);
  const { attemptsInfo } = useDSTMetaInfoStore();
  const { quizUID, goalUID, goalSlug } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isFree = quizDetails.collection?.programme?.is_special;

  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const selectedLanguage = useSelectorWithShallowEqual(getSelectedLanguage);
  const isQuizForPlusUser = useSelectorWithShallowEqual(
    getIsQuizForPlusUsers(quizUID)
  );

  const isUserEnrolledForQuiz = useSelectorWithShallowEqual(
    getIsUserEnrolledInQuiz(quizUID)
  );

  const { hasOngoingSectional, isLastOngoingSection, onGoingSectionUID } =
    useOngoingSection();
  const { forceProcessAPISImmediately } = useFailedAPIQueue();
  const segmentAnalytics = useQuizSegmentAnalytics();
  const { attemptedQuestionCount, skippedQuestionCount } = useTotalSummary();
  const isGTP = useConstants(getIsGTPGoal(quizDetails?.goal_uid));

  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);

  const { open: showSnackbar } = useSnackbar();

  const eligibilityType = () => {
    if (isDSTFlow) {
      return 'Scholarship Test';
    }
    if (isFree) {
      return 'Free Test';
    }
    return 'Plus Test';
  };

  const sendAnalyticsOnFinish = (props = {}) => {
    const defaultProps = {
      is_reattempt: !!quizDetails?.previous_sessions?.length,
      language: selectedLanguage?.display_name,
      is_plus: isQuizForPlusUser,
      is_enrolled: isUserEnrolledForQuiz,
      is_sellable: quizDetails.is_sellable,
      ...props
    };
    if (!isSubjective) {
      defaultProps.attempted_questions = attemptedQuestionCount;
      defaultProps.num_questions_skipped = skippedQuestionCount;
    }
    if (isGTP) {
      defaultProps.test_eligibility_type = eligibilityType();
    }
    segmentAnalytics(TEST_FINISHED, defaultProps);
  };

  const handleEndTest = async () => {
    const { error } = await dispatch(
      finishQuiz(quizUID, currentSessionUID, isDSTFlow)
    );

    if (error) {
      showSnackbar({
        message: error.message || 'Unable to end the quiz. Please try again!',
        type: 'error'
      });
      throw error;
    }
  };

  const handleCleanUpAfterFinish = async () => {
    // Order should be maintained
    await dispatch(fetchQuizMinimalData(quizUID));
    dispatch(cleanUpAfterQuizEnds());
    sendAnalyticsOnFinish();
  };

  const handleOnExpire = useCallback(async () => {
    await forceProcessAPISImmediately();

    if (hasOngoingSectional && !isLastOngoingSection) {
      const { error } = await dispatch(
        finishSection(quizUID, currentSessionUID, onGoingSectionUID)
      );

      if (error) {
        showSnackbar({
          message:
            error.message || 'Unable to finish the section. Please try again!',
          type: 'error'
        });
      }

      return;
    }
    try {
      await handleEndTest();
    } catch {
      return;
    }

    if (isDSTFlow) {
      sendAnalyticsOnFinish({
        is_reattempt: attemptsInfo?.attemptsLeft < 2
      });
      return pushRoute(
        {
          pathname:
            '/goal/[goalSlug]/[goalUID]/scholarship-test/[quizUID]/attempts/[sessionUID]',
          query: {
            goalSlug,
            goalUID,
            quizUID,
            sessionUID: currentSessionUID
          }
        },
        undefined,
        { shallow: true }
      );
    }

    handleCleanUpAfterFinish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onGoingSectionUID, attemptedQuestionCount, skippedQuestionCount]);

  return {
    finishQuiz: handleOnExpire,
    handleEndTest,
    sendAnalyticsOnFinish,
    handleCleanUpAfterFinish
  };
};

export default useFinishQuiz;
